import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  formatDateWithTime,
  hasPermission,
} from '@/shared';
import {
  useFetchCountriesFetch,
  useUpdateCountry,
} from '@/shared/actions/erp/countries.actions';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { Country } from '@/shared/interfaces/erp/system-settings';
import { useUiConfirmModalStore } from '@/store/ui';

export type CountriesPageProps = {};

const returnUrlCountries = ROUTER_PATHS.administration.paisesNav;

const CountriesPage: React.FC<CountriesPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_iva);

  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: countriesRes,
    isLoading,
    isRefetching,
  } = useFetchCountriesFetch({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updateCountry = useUpdateCountry(navigate, returnUrlCountries);

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Country>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
      },
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        enableSorting: false,
      },
      {
        accessorKey: 'nacionalidad',
        header: 'Nacionalidad',
        size: 200,
      },

      {
        accessorKey: 'cobertura',
        header: 'Cobertura',
        size: 200,
        enableColumnFilter: false,
        // filterVariant: 'select',
        // enableSorting: false,
        // filterSelectOptions: [
        //   { label: 'SI', value: true },
        //   { label: 'NO', value: false },
        // ],
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.cobertura}
              titulo={row.original.cobertura ? 'SI' : 'NO'}
              setEstadoSwitch={value => {
                if (!hasPermission(PermissionsEnum.api_change_pais)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado Cobertura',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateCountry.mutate({
                      id: row.original?.id_pais!,
                      data: { cobertura: !!value } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={router => {
                if (!hasPermission(PermissionsEnum.api_change_pais)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateCountry.mutate({
                      id: row.original.id_pais!,
                      data: { ...row.original, estado: !!router },
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : 'N/A',
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : 'N/A',
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (original: Country) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Editar País',
      subtitle: '¿Está seguro que desea editar este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrlCountries}/editar/${original.id_pais}`);
      },
    });
  };

  // const onDelete = (original: Country) => {
  //   setConfirmDialog({
  //     isOpen: true,
  //     subtitle: '¿Está seguro que desea eliminar el País seleccionado?',
  //     title: 'Confirmar eliminación',
  //     onConfirm: () => {
  //       deleteCountryMutation.mutate(original.id_pais!);
  //     },
  //     onClose() {
  //       setConfirmDialogIsOpen(false);
  //     },
  //   });
  // };

  return (
    <SingleTableBoxScene
      title="Países"
      urlRedirect={`${returnUrlCountries}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_pais)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="país por nombre"
      />

      <GeneralTable<Country>
        columns={columns}
        data={countriesRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // onGlobalFilterChange={setGlobalFilter}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={countriesRes?.data?.count}
        // // actions ========
        enableColumnActions={hasPermission(PermissionsEnum.api_change_pais)}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        positionActionsColumn="first"
        // crud
        onEdit={onEdit}
        canEdit={hasPermission(PermissionsEnum.api_change_pais)}
        canDelete={false}
        // onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default CountriesPage;
